/**
 * iiaSiteContextBridge — Image_Intelligence_Analyzer IIA-2 / IIA-3
 * SiteContext surfacing helper.
 *
 * Pure projection: takes a SiteContext + the SiteDocuments that carry
 * an `iiaResult`, and APPENDS one `'image-observation'` claim per doc
 * whose `iiaResult.observation` is present and not already mirrored.
 *
 * Why a separate helper:
 *   - sedBootstrap.ts must stay thin; this lets the bootstrap call a
 *     single function instead of inlining the projection.
 *   - The claim shape (`source: 'document-extracted'`, sourceDocId,
 *     userApproved: false) must match the existing PL3 / SA6 contract
 *     for `ExtractedClaim`; isolating the projection here makes the
 *     mapping visible + testable.
 *   - The deduplication invariant ("one image-observation per docId")
 *     is enforced here so callers can call it idempotently on every
 *     SED rebuild without growing claims[] unbounded.
 *
 * Pack §IIA-3 contract: the IIA-3 free-text path writes to
 * `siteContext.claims[]` with:
 *   - `kind: 'image-observation'` (the legacy `category` field —
 *     mapped to `'general'` since `ClaimCategory` doesn't include a
 *     bespoke `'image-observation'` member; the `versionNote` field
 *     carries the IIA-3 attribution),
 *   - `userApproved: false` (rendered via the absence of
 *     `state: 'authored'` AND a `versionNote` marker),
 *   - `sourceDocId` pointing at the image doc.
 *
 * Soft-fail: when the SiteContext is undefined, the function is a
 * no-op (returns 0). When a doc's `iiaResult.observation` is absent,
 * that doc is skipped.
 */
import type { SiteDocument } from '../../types/siteRag';
import type { SiteContext, ExtractedClaim } from '../../types/siteContext';

/** Marker used in `ExtractedClaim.versionNote` so the diff / audit UI
 *  can identify IIA-3 free-text claims without expanding the
 *  `ClaimCategory` enum. */
export const IIA_OBSERVATION_VERSION_NOTE = 'iia-3:image-observation';

/**
 * Append one `image-observation` claim per SiteDocument whose
 * `iiaResult.observation` is present and not already mirrored on the
 * SiteContext (looked up by `sourceDocId`).
 *
 * Mutates `siteContext.claims[]` in place. Returns the number of
 * claims newly appended (0 when every doc's observation was already
 * surfaced).
 *
 * Pure (no async, no I/O); safe to call repeatedly — duplicate
 * detection is by `sourceDocId + versionNote` so a re-bootstrap on
 * the same site is a no-op.
 */
export function applyIiaObservationsToSiteContext(
  siteContext: SiteContext,
  imageDocs: readonly SiteDocument[],
): number {
  if (!siteContext) return 0;
  if (!Array.isArray(siteContext.claims)) {
    // Defensive — the legacy SiteContext type tolerates an empty
    // claims array but we should never silently mint one without the
    // caller's consent. Bail with 0.
    return 0;
  }
  // Build the dedupe set: existing claims keyed by (sourceDocId,
  // versionNote). One docId can carry at most one IIA observation.
  const seen = new Set<string>();
  for (const c of siteContext.claims) {
    if (c.versionNote === IIA_OBSERVATION_VERSION_NOTE && c.sourceDocId) {
      seen.add(c.sourceDocId);
    }
  }

  let added = 0;
  for (const doc of imageDocs) {
    const iia = doc.iiaResult;
    if (!iia || !iia.observation) continue;
    if (seen.has(doc.id)) continue;
    const observationText = String(iia.observation.observationText ?? '').trim();
    if (!observationText) continue;

    const now = new Date().toISOString();
    // IIA-3 confidence preference: the observation carries its own
    // post-cap confidence (≤ 0.55 per pack §IIA-3). When absent
    // (pre-IIA-3 docs), fall back to the classifier confidence so the
    // claim still gets a sane percent rather than a NaN. Strict
    // Number.isFinite guard per HARD RULE — a poisoned classifier
    // payload (NaN / +Infinity) must not propagate as a confidence
    // chip.
    const rawObsConf = iia.observation?.confidence;
    const rawClsConf = iia.classification?.confidence;
    const obsConf = Number.isFinite(rawObsConf) ? (rawObsConf as number) : NaN;
    const clsConf = Number.isFinite(rawClsConf) ? (rawClsConf as number) : 0;
    const confidence01 = Number.isFinite(obsConf)
      ? Math.max(0, Math.min(1, obsConf))
      : Math.max(0, Math.min(1, clsConf));
    // The claim id is deterministic on (docId, versionNote) so re-runs
    // are idempotent even across an undo + redo of the bootstrap.
    const claim: ExtractedClaim = {
      id: `iia-${doc.id}`,
      category: 'general',
      field: 'imageObservation',
      sourceSpan: observationText.slice(0, 240),
      claim: observationText.slice(0, 120),
      extractorConfidence: Math.round(confidence01 * 100),
      versionNote: IIA_OBSERVATION_VERSION_NOTE,
      writtenBy: 'system:iia-vision',
      source: 'document-extracted',
      sourceDocId: doc.id,
      lastWrittenAt: now,
    };
    siteContext.claims.push(claim);
    seen.add(doc.id);
    added += 1;
  }
  return added;
}
